//  Push messaging consists of three components:
//
//  1.  On the server, we use the FCM HTTP API to push new messages
//  2.  Service Worker receives these messages, and it will show a notification
//      message when app is in background; data messages are forwarded here, as
//      are notification messages received when app in foreground
//  3.  This component will either show notification message in-app (since app
//      is in foreground), or act on data message
//
// Hybrid apps use the https://github.com/phonegap/phonegap-plugin-push API.


import * as cordovaCallbacks from './cordova';
import * as UI               from '../ui';
import * as User             from '../user';
import * as webCallbacks     from './web';
import { isCordova }         from '../util/env';
import dispatchAction        from './dispatch_action';
import onChange              from '../util/on_change';
import rollbar               from '../rollbar';
import store                 from '../store';


// We call the provider callback only once, and always with the same
// event callbacks (onToken, onDataMessage, etc).  Cordova needs to register
// event listeners each time, Firebase only once.

export async function enableNotifications() {
  const callbacks = getPlatformCallbacks();
  if (callbacks)
    await callbacks.enableNotifications({ onToken, onNotification, onDataMessage });
}

export async function disableNotifications() {
  const callbacks = getPlatformCallbacks();
  if (callbacks)
    await callbacks.disableNotifications();
}

export async function getNotificationState() {
  const callbacks = getPlatformCallbacks();
  if (callbacks)
    return await callbacks.getNotificationState();
  else
    return null;
}

let pushNotificationsInitialized = false;
export async function initializePushNotifications() {
  if (pushNotificationsInitialized)
    return;
  pushNotificationsInitialized = true;
  await enableNotifications();
  const badgeCount = User.getBadgeCount(store.getState());
  await setBadgeCount(badgeCount);
}

export async function setBadgeCount(number) {
  const callbacks = getPlatformCallbacks();
  if (callbacks)
    await callbacks.setBadgeCount({ number });
}

// When user signs out of the app, we want to stop showing them push
// notifications.   Emphasis on the "showing".
//
// We can ask the server to remove the device token, that way we're not sending
// push messages … if the request went through to the server, it updated the
// database, etc.  Distributed systems are hard.
//
// We can tell the browser to discard the device token, that's immediate and
// 100% likely to prevent push notifications from showing up.
//
// Note: if the user "signed out" by deleting their storage, then they'll still
// get push notification.
onChange(store, User.isSignedOut, async function(_, isSignedOut) {
  if (isSignedOut)
    disableNotifications();
});


onChange(store, User.getBadgeCount, async function(_, number) {
  setBadgeCount(number);
});


function getPlatformCallbacks() {
  // Check for Cordova first.  Currently PushManager not available on mobile,
  // but if it does show up one day unannounced, the code may fail if we don't
  // prepare.
  if (isCordova()) {
    const isSupported = ('PushNotification' in window);
    if (isSupported)
      return cordovaCallbacks;
    else {
      // iOS 10.2 seems to not support them.
      // https://github.com/broadly/broadly/issues/3026
      rollbar.error(new Error('Push Notifications not supported on Cordova'));
      return {
        enableNotifications() {},
        disableNotifications() {},
        getNotificationState() {},
        setBadgeCount() {}
      };
    }
  } else
    return webCallbacks;
}


function onToken(deviceToken) {
  if (deviceToken)
    store.dispatch(User.newDeviceToken(deviceToken));
}

function onNotification({ title, body, url }) {
  store.dispatch(UI.setNotification({ title, body, url }));
}

function onDataMessage(json) {
  try {
    // Looks like Cordova parses this JSON on Android, but not on iOS; Firebase
    // always passes the document as is.
    const message = (typeof json === 'string') ? JSON.parse(json) : json;

    // TODO: Should check message is for this user?
    // const userID  = User.getUserID(store.getState());
    // if (message.action && userID === message.userID)
    if (message.action) {
      const action = (typeof message.action === 'string') ? JSON.parse(message.action) : message.action;
      store.dispatch(dispatchAction(action));
    }
  } catch (error) {
    rollbar.error(error);
  }
}
